import { inject, Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { IncidentsDetailedPlaceType } from '../models/enums/incidents';
import { RouterUtils } from '../utils/reducers/router.utils';
import { ArrayUtils } from '@festo-ax/smartenance-shared-ui';
import { EventsDetailedPlaceType } from '../models/enums/events';

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private router: Router = inject(Router);

  /**
   * Guards against NG04008 ("path contains undefined segment").
   * Returns false if the command array is empty or holds any falsy segment.
   */
  private isValidRoute(primary: (string | undefined)[] | undefined, context: string): boolean {
    if (!ArrayUtils.isNotEmpty(primary)) {
      console.error(`${context}: route command array is empty or undefined`);
      return false;
    }

    if (primary.some((segment: string | undefined): boolean => !segment)) {
      console.error(`${context}: route command array contains undefined or empty segments`, primary);
      return false;
    }

    return true;
  }

  navigateToIncidentEdit(incidentId: string, machineId?: string): void {
    const url: string = this.router.url;
    const placeOfAction: IncidentsDetailedPlaceType = RouterUtils.getIncidentDetailedPlaceType(url);
    let primary: string[];

    switch (placeOfAction) {
      case IncidentsDetailedPlaceType.ALL_INCIDENTS:
      case IncidentsDetailedPlaceType.AI_INCIDENTS:
      case IncidentsDetailedPlaceType.MY_AREA_OPEN_INCIDENTS:
      case IncidentsDetailedPlaceType.MY_AREA_BOOKMARKED_INCIDENTS:
        primary = RouterUtils.getIncidentEditRoutingData(placeOfAction, incidentId).primary;
        break;
      case IncidentsDetailedPlaceType.MACHINE_LOGBOOK_INCIDENTS:
      case IncidentsDetailedPlaceType.MACHINE_INCIDENTS:
        primary = RouterUtils.getIncidentEditRoutingData(placeOfAction, incidentId, machineId).primary;
        break;
      default:
        console.error('NavigationService.navigateToIncidentEdit: cannot determine place type');
        primary = RouterUtils.getIncidentEditRoutingData(IncidentsDetailedPlaceType.ALL_INCIDENTS, incidentId).primary;
        break;
    }

    this.router.navigate(this.isValidRoute(primary, 'NavigationService.navigateToIncidentEdit') ? primary : ['/']);
  }

  navigateToIncidentDetails(incidentId: string, machineId?: string): void {
    const url: string = this.router.url;
    const placeOfAction: IncidentsDetailedPlaceType = RouterUtils.getIncidentDetailedPlaceType(url);
    let primary: string[] | undefined;

    switch (placeOfAction) {
      case IncidentsDetailedPlaceType.ALL_INCIDENTS:
      case IncidentsDetailedPlaceType.AI_INCIDENTS:
      case IncidentsDetailedPlaceType.MY_AREA_OPEN_INCIDENTS:
      case IncidentsDetailedPlaceType.MY_AREA_BOOKMARKED_INCIDENTS:
        primary = RouterUtils.getIncidentDetailRoutingData(placeOfAction, incidentId)?.primary;
        break;
      case IncidentsDetailedPlaceType.MACHINE_LOGBOOK_INCIDENTS:
      case IncidentsDetailedPlaceType.MACHINE_INCIDENTS:
        primary = RouterUtils.getIncidentDetailRoutingData(placeOfAction, incidentId, machineId)?.primary;
        break;
      default:
        console.error('NavigationService.navigateToIncidentDetails: cannot determine place type');
        primary = RouterUtils.getIncidentDetailRoutingData(IncidentsDetailedPlaceType.ALL_INCIDENTS, incidentId)?.primary;
        break;
    }

    this.router.navigate(this.isValidRoute(primary, 'NavigationService.navigateToIncidentDetails') ? primary! : ['/']);
  }

  navigateToSimilarIncidents(incidentId: string): void {
    void this.router.navigate(['/incidents', 'all', 'ai-incidents'], { queryParams: { similarTo: incidentId } });
  }

  navigateToRegularIncidentsList(): void {
    void this.router.navigate(['/incidents', 'all', 'incidents']);
  }

  navigateToAiIncidentsList(): void {
    void this.router.navigate(['/incidents', 'all', 'ai-incidents']);
  }

  navigateToServiceUnavailable(): void {
    if (this.router.url === '/service-unavailable') {
      return;
    }

    void this.router.navigateByUrl('/service-unavailable');
  }

  navigateToIncidents(machineId?: string, queryParams?: Params): void {
    const url: string = this.router.url;
    const placeOfAction: IncidentsDetailedPlaceType = RouterUtils.getIncidentDetailedPlaceType(url);
    let primary: string[] | undefined;

    switch (placeOfAction) {
      case IncidentsDetailedPlaceType.ALL_INCIDENTS:
      case IncidentsDetailedPlaceType.AI_INCIDENTS:
      case IncidentsDetailedPlaceType.MY_AREA_OPEN_INCIDENTS:
      case IncidentsDetailedPlaceType.MY_AREA_BOOKMARKED_INCIDENTS:
        primary = RouterUtils.getIncidentsRoutingData(placeOfAction).primary;
        break;
      case IncidentsDetailedPlaceType.MACHINE_LOGBOOK_INCIDENTS:
      case IncidentsDetailedPlaceType.MACHINE_INCIDENTS:
        primary = RouterUtils.getIncidentsRoutingData(placeOfAction, machineId)?.primary;
        break;
      default:
        console.error('NavigationService.navigateToIncidents: cannot determine place type');
        primary = RouterUtils.getIncidentsRoutingData(IncidentsDetailedPlaceType.ALL_INCIDENTS)?.primary;
        break;
    }

    this.router.navigate(
      this.isValidRoute(primary, 'NavigationService.navigateToIncidents') ? primary! : ['/'],
      queryParams && { queryParams },
    );
  }

  navigateToEventEdit(eventId: string, machineId?: string): void {
    const url: string = this.router.url;
    const placeOfAction: EventsDetailedPlaceType = RouterUtils.getEventDetailedPlaceType(url);
    let primary: string[];

    switch (placeOfAction) {
      case EventsDetailedPlaceType.ALL_EVENTS:
        primary = RouterUtils.getEventEditRoutingData(placeOfAction, eventId).primary;
        break;
      case EventsDetailedPlaceType.MACHINE_LOGBOOK_EVENTS:
      case EventsDetailedPlaceType.MACHINE_EVENTS:
        primary = RouterUtils.getEventEditRoutingData(placeOfAction, eventId, machineId).primary;
        break;
      default:
        console.error('NavigationService.navigateToEventEdit: cannot determine place type');
        primary = RouterUtils.getEventEditRoutingData(EventsDetailedPlaceType.ALL_EVENTS, eventId).primary;
        break;
    }

    this.router.navigate(this.isValidRoute(primary, 'NavigationService.navigateToEventEdit') ? primary : ['/']);
  }

  calculateEventDetailPrimaryRoute(eventId: string, machineId?: string): string[] {
    const url: string = this.router.url;
    const placeOfAction: EventsDetailedPlaceType = RouterUtils.getEventDetailedPlaceType(url);
    let primary: string[];

    switch (placeOfAction) {
      case EventsDetailedPlaceType.ALL_EVENTS:
        primary = RouterUtils.getEventDetailRoutingData(placeOfAction, eventId).primary;
        break;
      case EventsDetailedPlaceType.MACHINE_LOGBOOK_EVENTS:
      case EventsDetailedPlaceType.MACHINE_EVENTS:
        primary = RouterUtils.getEventDetailRoutingData(placeOfAction, eventId, machineId).primary;
        break;
      default:
        console.error('NavigationService.calculateEventDetailPrimaryRoute: cannot determine place type');
        primary = RouterUtils.getEventDetailRoutingData(EventsDetailedPlaceType.ALL_EVENTS, eventId).primary;
        break;
    }

    return this.isValidRoute(primary, 'NavigationService.calculateEventDetailPrimaryRoute') ? primary : ['/'];
  }

  navigateToEventDetail(eventId: string, machineId?: string): void {
    const primary: string[] = this.calculateEventDetailPrimaryRoute(eventId, machineId);

    this.router.navigate(ArrayUtils.isNotEmpty(primary) ? primary : ['/']);
  }

  navigateToEvents(machineId?: string, queryParams?: Params): void {
    const url: string = this.router.url;
    const placeOfAction: EventsDetailedPlaceType = RouterUtils.getEventDetailedPlaceType(url);
    let primary: string[];

    switch (placeOfAction) {
      case EventsDetailedPlaceType.ALL_EVENTS:
        primary = RouterUtils.getEventsRoutingData(placeOfAction).primary;
        break;
      case EventsDetailedPlaceType.MACHINE_LOGBOOK_EVENTS:
      case EventsDetailedPlaceType.MACHINE_EVENTS:
        primary = RouterUtils.getEventsRoutingData(placeOfAction, machineId).primary;
        break;
      default:
        console.error('NavigationService.navigateToEvents: cannot determine place type');
        primary = RouterUtils.getEventsRoutingData(EventsDetailedPlaceType.ALL_EVENTS).primary;
        break;
    }

    this.router.navigate(
      this.isValidRoute(primary, 'NavigationService.navigateToEvents') ? primary : ['/'],
      queryParams && { queryParams },
    );
  }
}
